import React, { useState } from 'react';
import { Edit, Trash2, Download, ArrowRightLeft, Loader2, ShieldCheck, FileText, Undo2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { format } from 'date-fns';
import { toast } from '../../hooks/use-toast';
import {
  previewPartialPaid,
  overrideToPartialPaid,
  previewProbationPayment,
  authorizeProbationPayment,
} from '../../services/api';

const getLeaveTypeColor = (type) => {
  const colors = {
    annual: 'bg-blue-100 text-blue-800',
    sick: 'bg-red-100 text-red-800',
    emergency: 'bg-orange-100 text-orange-800',
    unpaid: 'bg-gray-100 text-gray-800',
    mgmt_unpaid: 'bg-amber-100 text-amber-800 border border-amber-300',
    partial_unpaid: 'bg-violet-100 text-violet-800',
    maternity: 'bg-pink-100 text-pink-800',
    paternity: 'bg-green-100 text-green-800',
    public_holiday: 'bg-purple-100 text-purple-800',
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
};

const getLeaveTypeLabel = (type) => {
  const labels = {
    annual: 'annual',
    sick: 'sick',
    emergency: 'emergency',
    unpaid: 'unpaid',
    mgmt_unpaid: 'MGMT unpaid',
    partial_unpaid: 'partial unpaid',
    maternity: 'maternity',
    paternity: 'paternity',
    public_holiday: 'public_holiday',
  };
  return labels[type] || type;
};

const LeaveRecordsList = ({ filteredLeaves, employees, onEdit, onDelete, onDownloadLeaveSalary, isViewer, onDataRefresh }) => {
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);

  // ── Probation Payment Authorization ──
  const [probationDialogOpen, setProbationDialogOpen] = useState(false);
  const [probationStep, setProbationStep] = useState('warning'); // 'warning' | 'preview'
  const [probationData, setProbationData] = useState(null);
  const [probationLoading, setProbationLoading] = useState(false);
  const [probationAuthLoading, setProbationAuthLoading] = useState(false);
  const [probationLeaveCtx, setProbationLeaveCtx] = useState(null);

  const [appDownloading, setAppDownloading] = useState(null);
  const [undoing, setUndoing] = useState(null);

  const isUpcoming = (leave) => {
    try {
      const start = new Date(String(leave.start_date).split('T')[0]);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return start > today;
    } catch { return false; }
  };
  const canUndo = (leave) =>
    isUpcoming(leave) && ['approved', 'completed', 'taken'].includes((leave.status || '').toLowerCase());

  const undoApproval = async (leave) => {
    if (!window.confirm(`Undo approval for this upcoming leave?\n\nIt will move to "Pending Leave Approvals" for a super admin to re-approve, and the concerned manager / HR will be notified.`)) return;
    setUndoing(leave.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leaves/${leave.id}/undo-approval`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Undo failed'); }
      toast({ title: 'Approval undone', description: 'Leave moved back to Pending Approvals.' });
      if (onDataRefresh) onDataRefresh();
    } catch (e) {
      toast({ title: 'Could not undo', description: e.message || '', variant: 'destructive' });
    } finally { setUndoing(null); }
  };

  const downloadApplication = async (leave) => {
    setAppDownloading(leave.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/leaves/${leave.id}/application/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `LeaveApplication_${(leave.employee_name || 'Employee').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not download leave application', variant: 'destructive' });
    } finally {
      setAppDownloading(null);
    }
  };

  const fmtDate = (v) => {
    if (!v) return null;
    try { return format(new Date(v), 'dd MMM yyyy'); } catch { return String(v).split('T')[0]; }
  };
  const statusMeta = (s) => {
    const k = (s || 'pending').toLowerCase();
    if (['approved', 'completed', 'taken'].includes(k)) return { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700' };
    if (k === 'rejected') return { label: 'Rejected', cls: 'bg-rose-100 text-rose-700' };
    if (k === 'cancelled') return { label: 'Cancelled', cls: 'bg-gray-200 text-gray-600' };
    return { label: 'Pending', cls: 'bg-amber-100 text-amber-700' };
  };

  const handleProbationStart = (leave) => {
    setProbationLeaveCtx(leave);
    setProbationData(null);
    setProbationStep('warning');
    setProbationDialogOpen(true);
  };

  const handleProbationAcknowledge = async () => {
    if (!probationLeaveCtx) return;
    setProbationLoading(true);
    try {
      const res = await previewProbationPayment(probationLeaveCtx.id);
      setProbationData(res.data);
      setProbationStep('preview');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to load probation preview';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
      setProbationDialogOpen(false);
    } finally {
      setProbationLoading(false);
    }
  };

  const handleProbationConfirm = async () => {
    if (!probationLeaveCtx) return;
    setProbationAuthLoading(true);
    try {
      const res = await authorizeProbationPayment(probationLeaveCtx.id);
      toast({
        title: 'Probation payment authorized',
        description: `${res.data.paid_days} day(s) paid + ${res.data.unpaid_days} day(s) unpaid.`,
      });
      setProbationDialogOpen(false);
      setProbationData(null);
      setProbationLeaveCtx(null);
      if (onDataRefresh) onDataRefresh();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to authorize probation payment';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setProbationAuthLoading(false);
    }
  };

  const handleOverridePreview = async (leaveId) => {
    setSelectedLeaveId(leaveId);
    setPreviewLoading(true);
    setOverrideDialogOpen(true);
    try {
      const response = await previewPartialPaid(leaveId);
      setPreviewData(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to load preview';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
      setOverrideDialogOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmOverride = async () => {
    if (!selectedLeaveId) return;
    setOverrideLoading(true);
    try {
      const response = await overrideToPartialPaid(selectedLeaveId);
      toast({ title: 'Success', description: response.data.message });
      setOverrideDialogOpen(false);
      setPreviewData(null);
      if (onDataRefresh) onDataRefresh();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to override leave';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setOverrideLoading(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Leave Records</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {!filteredLeaves || filteredLeaves.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No leave records found</p>
            ) : (
              filteredLeaves.map((leave) => {
                const emp = (employees || []).find(e => e.id === leave.employee_id);
                const joinDateStr = emp?.join_date;
                let wasOnProbation = false;
                if (joinDateStr && leave.start_date) {
                  const joinDate = new Date(joinDateStr);
                  const startDate = new Date(leave.start_date);
                  const probationEnd = new Date(joinDate);
                  probationEnd.setDate(probationEnd.getDate() + 180);
                  wasOnProbation = startDate < probationEnd;
                }
                // Effective type = what the leave originally was (before any auto-probation conversion)
                const effectiveType = leave.original_leave_type || leave.leave_type;
                const TYPES_NOT_ELIGIBLE_FOR_PROBATION_PAY = [
                  'public_holiday', 'mgmt_unpaid', 'partial_unpaid', 'unpaid',
                  'sick', 'paternity', 'maternity'
                ];
                const probationEligibleType = !TYPES_NOT_ELIGIBLE_FOR_PROBATION_PAY.includes(effectiveType);
                const showProbationWarning =
                  (leave.is_probation_leave || wasOnProbation) &&
                  !leave.probation_authorized &&
                  probationEligibleType;
                const showProbationShield = showProbationWarning;
                return (
                <div key={leave.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid={`leave-record-${leave.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{leave.employee_name}</p>
                    <p className="text-sm text-gray-600 mt-1">{leave.reason || 'No reason provided'}</p>
                    {leave.leave_type === 'annual' && leave.paid_days != null && leave.unpaid_days != null && (
                      <div className="mt-1">
                        <span
                          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                            leave.unpaid_days > 0
                              ? 'bg-rose-100 text-rose-700'
                              : 'bg-emerald-100 text-emerald-700'
                          }`}
                          data-testid={`leave-split-badge-${leave.id}`}
                        >
                          {leave.unpaid_days > 0
                            ? `${leave.paid_days} paid · ${leave.unpaid_days} unpaid (LOP)`
                            : `${leave.paid_days} paid · fully covered`}
                        </span>
                      </div>
                    )}
                    {leave.leave_type === 'partial_unpaid' && leave.paid_days != null && (
                      <p className="text-xs text-violet-600 mt-1 font-medium" data-testid={`partial-info-${leave.id}`}>
                        {leave.paid_days} days paid (from accrued) + {leave.unpaid_days} days unpaid
                      </p>
                    )}
                    {showProbationWarning && (
                      <p className="text-xs text-amber-700 mt-1 font-medium" data-testid={`probation-warning-${leave.id}`}>
                        ⚠ Probation period — eligible for HR-authorized partial pay (2 days/month accrual)
                      </p>
                    )}
                    {leave.probation_authorized && (
                      <p className="text-xs text-emerald-700 mt-1 font-medium" data-testid={`probation-authorized-${leave.id}`}>
                        ✓ Probation HR-authorized: {leave.probation_paid_days} day(s) paid + {leave.probation_unpaid_days} day(s) unpaid
                      </p>
                    )}
                    {/* Application / approval metadata */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-500" data-testid={`leave-meta-${leave.id}`}>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${statusMeta(leave.status).cls}`} data-testid={`leave-status-${leave.id}`}>
                        {statusMeta(leave.status).label}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${
                        (leave.source === 'portal' || leave.applied_by === 'employee') ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'
                      }`}>
                        {(leave.source === 'portal' || leave.applied_by === 'employee') ? 'Applied online by employee' : 'Entered by HR/Admin'}
                      </span>
                      {fmtDate(leave.applied_at || leave.created_at) && (
                        <span>Applied: <b className="text-gray-600">{fmtDate(leave.applied_at || leave.created_at)}</b></span>
                      )}
                      {(leave.approved_by_manager || leave.actioned_by || leave.approved_by) && (
                        <span>{statusMeta(leave.status).label === 'Rejected' ? 'Rejected' : 'Approved'} by: <b className="text-gray-600">{leave.approved_by_manager || leave.actioned_by || leave.approved_by}</b>{leave.approved_by_manager ? ' (Manager)' : ''}</span>
                      )}
                      {fmtDate(leave.actioned_at || leave.approved_at) && (
                        <span>on <b className="text-gray-600">{fmtDate(leave.actioned_at || leave.approved_at)}</b></span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {format(new Date(leave.start_date), 'MMM dd')} - {format(new Date(leave.end_date), 'MMM dd, yyyy')}
                      </p>
                      <p className="text-xs text-gray-600">{leave.days} days</p>
                    </div>
                    <Badge className={getLeaveTypeColor(leave.leave_type)} data-testid={`leave-type-badge-${leave.id}`}>
                      {getLeaveTypeLabel(leave.leave_type)}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadApplication(leave)}
                      disabled={appDownloading === leave.id}
                      title="Download Leave Application (PDF)"
                      data-testid={`download-application-${leave.id}`}
                    >
                      {appDownloading === leave.id
                        ? <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
                        : <FileText className="h-4 w-4 text-violet-600" />}
                    </Button>
                    {!isViewer && (
                      <div className="flex space-x-1">
                        {canUndo(leave) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => undoApproval(leave)}
                            disabled={undoing === leave.id}
                            title="Undo approval (send back to Pending for manager approval)"
                            data-testid={`undo-approval-${leave.id}`}
                          >
                            {undoing === leave.id
                              ? <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                              : <Undo2 className="h-4 w-4 text-amber-600" />}
                          </Button>
                        )}
                        {showProbationShield && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleProbationStart(leave)}
                            title="Authorize Probation Leave Payment"
                            data-testid={`authorize-probation-${leave.id}`}
                          >
                            <ShieldCheck className="h-4 w-4 text-emerald-600" />
                          </Button>
                        )}
                        {leave.leave_type === 'unpaid' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOverridePreview(leave.id)}
                            title="Override to Partial Paid"
                            data-testid={`override-partial-paid-${leave.id}`}
                          >
                            <ArrowRightLeft className="h-4 w-4 text-violet-600" />
                          </Button>
                        )}
                        {onDownloadLeaveSalary && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onDownloadLeaveSalary(leave)}
                            title="Download Leave Salary"
                            data-testid={`download-leave-salary-${leave.id}`}
                          >
                            <Download className="h-4 w-4 text-orange-600" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onEdit(leave)}
                          data-testid={`edit-leave-${leave.id}`}
                        >
                          <Edit className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDelete(leave.id)}
                          data-testid={`delete-leave-${leave.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Override to Partial Paid Dialog */}
      <Dialog open={overrideDialogOpen} onOpenChange={(open) => { if (!open) { setOverrideDialogOpen(false); setPreviewData(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-violet-600" />
              Override to Partial Paid
            </DialogTitle>
          </DialogHeader>
          
          {previewLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
              <span className="ml-2 text-gray-600">Calculating accrued balance...</span>
            </div>
          ) : previewData ? (
            <div className="space-y-4" data-testid="partial-paid-preview">
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                <p className="font-medium text-violet-900 mb-2">{previewData.employee_name}</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-600">Join Date:</span>
                  <span className="font-medium">{format(new Date(previewData.join_date), 'MMM dd, yyyy')}</span>
                  <span className="text-gray-600">Leave Period:</span>
                  <span className="font-medium">{format(new Date(previewData.leave_start_date), 'MMM dd')} - {format(new Date(previewData.leave_end_date), 'MMM dd, yyyy')}</span>
                  <span className="text-gray-600">Total Leave Days:</span>
                  <span className="font-medium">{previewData.total_leave_days} days</span>
                  <span className="text-gray-600">Days Since Joining:</span>
                  <span className="font-medium">{previewData.days_since_joining} days</span>
                  <span className="text-gray-600">Accrual Rate:</span>
                  <span className="font-medium">{previewData.accrual_rate} days/month</span>
                </div>
              </div>

              <div className="bg-gray-50 border rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Balance Calculation</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-600">Accrued Entitlement:</span>
                  <span className="font-medium">{previewData.accrued_entitlement} days</span>
                  <span className="text-gray-600">Annual Already Taken:</span>
                  <span className="font-medium text-red-600">-{previewData.annual_already_taken} days</span>
                  <span className="text-gray-600">Available Balance:</span>
                  <span className="font-bold text-green-700">{previewData.available_balance} days</span>
                </div>
              </div>

              <div className="bg-white border-2 border-violet-300 rounded-lg p-4">
                <p className="text-sm font-medium text-violet-800 mb-3">Override Breakdown</p>
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-green-700">{previewData.paid_days}</p>
                    <p className="text-xs text-gray-600 mt-1">Days Paid<br />(from accrued balance)</p>
                  </div>
                  <div className="text-2xl text-gray-300 px-3">+</div>
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-gray-600">{previewData.unpaid_days}</p>
                    <p className="text-xs text-gray-600 mt-1">Days Unpaid<br />(remaining)</p>
                  </div>
                  <div className="text-2xl text-gray-300 px-3">=</div>
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-violet-700">{previewData.total_leave_days}</p>
                    <p className="text-xs text-gray-600 mt-1">Total Days</p>
                  </div>
                </div>
              </div>

              {!previewData.can_override && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  Cannot override: No accrued balance available (0 days).
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setOverrideDialogOpen(false); setPreviewData(null); }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmOverride}
                  disabled={!previewData.can_override || overrideLoading}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="confirm-override-btn"
                >
                  {overrideLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying...</>
                  ) : (
                    'Confirm Override'
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Probation Payment Authorization Dialog */}
      <Dialog
        open={probationDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setProbationDialogOpen(false);
            setProbationData(null);
            setProbationLeaveCtx(null);
            setProbationStep('warning');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              {probationStep === 'warning' ? 'Probation Period Notice' : 'Authorize Probation Payment'}
            </DialogTitle>
          </DialogHeader>

          {probationStep === 'warning' && probationLeaveCtx && (
            <div className="space-y-4" data-testid="probation-warning-step">
              <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                <p className="font-semibold text-amber-900 mb-2">⚠ Employee is under probation</p>
                <p className="text-sm text-amber-800 leading-relaxed">
                  <span className="font-medium">{probationLeaveCtx.employee_name}</span> was on probation
                  when this leave was taken ({format(new Date(probationLeaveCtx.start_date), 'MMM dd')} – {format(new Date(probationLeaveCtx.end_date), 'MMM dd, yyyy')}) and is <span className="font-semibold">not eligible for standard leave entitlement</span>.
                </p>
                <p className="text-sm text-amber-800 mt-2 leading-relaxed">
                  As an exception, you can authorize payment for the days the employee earned up to the leave start date, calculated at <span className="font-semibold">2 days per month of service</span>.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setProbationDialogOpen(false)}
                  data-testid="probation-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleProbationAcknowledge}
                  disabled={probationLoading}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="probation-acknowledge-btn"
                >
                  {probationLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating…</>
                  ) : (
                    'Acknowledge & Continue'
                  )}
                </Button>
              </div>
            </div>
          )}

          {probationStep === 'preview' && probationData && (
            <div className="space-y-4" data-testid="probation-preview-step">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="font-medium text-emerald-900 mb-2">{probationData.employee_name}</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                  <span className="text-gray-600">Join Date:</span>
                  <span className="font-medium">{format(new Date(probationData.join_date), 'MMM dd, yyyy')}</span>
                  <span className="text-gray-600">Leave Start:</span>
                  <span className="font-medium">{format(new Date(probationData.leave_start), 'MMM dd, yyyy')}</span>
                  <span className="text-gray-600">Service at Leave:</span>
                  <span className="font-medium">{probationData.months_decimal} months ({probationData.days_since_join} days)</span>
                  <span className="text-gray-600">Accrual Rate:</span>
                  <span className="font-medium">{probationData.accrual_rate_per_month} days / month</span>
                  <span className="text-gray-600">Earned Cap:</span>
                  <span className="font-medium text-emerald-700">{probationData.earned_days} day(s)</span>
                </div>
              </div>

              <div className="bg-white border-2 border-emerald-300 rounded-lg p-4">
                <p className="text-sm font-medium text-emerald-800 mb-3">Payment Split</p>
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-green-700" data-testid="probation-paid-days">
                      {probationData.paid_days}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">Days Paid<br />(probation accrual)</p>
                  </div>
                  <div className="text-2xl text-gray-300 px-3">+</div>
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-gray-600" data-testid="probation-unpaid-days">
                      {probationData.unpaid_days}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">Days Unpaid<br />(over the cap)</p>
                  </div>
                  <div className="text-2xl text-gray-300 px-3">=</div>
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-emerald-700">{probationData.leave_days}</p>
                    <p className="text-xs text-gray-600 mt-1">Total Days</p>
                  </div>
                </div>
              </div>

              {probationData.paid_days <= 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  Cannot authorize: employee has not yet accrued any payable days at this leave's start date.
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setProbationDialogOpen(false)}
                  data-testid="probation-preview-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleProbationConfirm}
                  disabled={probationData.paid_days <= 0 || probationAuthLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="probation-confirm-btn"
                >
                  {probationAuthLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Authorizing…</>
                  ) : (
                    'Authorize Payment'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LeaveRecordsList;
